import { ReactNode } from 'react';
import { useToggleState } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';
import { Icon, IconProps } from '../Icon';

export interface NoticeProps {
  open?: boolean;
  title?: ReactNode;
  className?: string;
  children?: ReactNode;
  hasCloseIcon?: boolean;
  description?: ReactNode;
  type: 'info' | 'success' | 'warning' | 'error' | 'none';
}

export const Notice = ({
  title,
  children,
  className,
  open = true,
  description,
  hasCloseIcon,
  type = 'none'
}: NoticeProps) => {
  const { status, toggleStatus } = useToggleState(open);

  const noticeVariant = noticeVariants[type];

  return status ? (
    <div
      className={cn(
        'relative box-border flex border-b-border border-l-[3px] bg-white px-4 py-3.5 break-words',
        noticeVariant?.twClass,
        className
      )}
    >
      {noticeVariant ? (
        <Icon icon={noticeVariant.icon} size={20} className="mr-3 self-center" variant={noticeVariant.variant} />
      ) : null}

      {title || description ? (
        <div className="flex flex-1 flex-col justify-center text-base leading-4">
          {title ? <div className="font-semibold">{title}</div> : null}
          {description ? (
            <div className={cn('mt-1 font-medium text-tertiary', title ? 'mt-1' : 'mt-0')}>{description}</div>
          ) : null}
        </div>
      ) : null}

      {children}

      {hasCloseIcon ? (
        <Icon
          className="absolute right-3 cursor-pointer hover:opacity-80"
          size={12}
          icon="close"
          variant="light-gray"
          onClick={toggleStatus}
        />
      ) : null}
    </div>
  ) : null;
};

const noticeVariants: Record<
  NoticeProps['type'],
  { icon: IconProps['icon']; variant: IconProps['variant']; twClass: string } | undefined
> = {
  info: {
    icon: 'info',
    variant: 'blue',
    twClass: cn('border-l-blue-70')
  },
  error: {
    icon: 'warning',
    variant: 'red',
    twClass: cn('border-l-red-50')
  },
  success: {
    icon: 'success',
    variant: 'green',
    twClass: cn('border-l-dark-green-70')
  },
  warning: {
    icon: 'warning',
    variant: 'yellow',
    twClass: cn('border-l-attention-yellow')
  },
  none: undefined
};
